import Button from "@components/Button";
import Glimmer, {GlimmerOffset} from "@components/Glimmer";
import {ApptSlot} from "@services/monolith/availability";
import {useTranslation} from "@i18n/client";
import React, {useMemo} from "react";
import {useDisclosure} from "src/hooks/useDisclosure";
import {useTwMediaQuery} from "src/hooks/useTwMediaQuery";
import {Breakpoint} from "src/constants/breakpoint";
import {I18nNamespace} from "@i18n";

import {ProviderHeader} from "./ProviderHeader";
import SlotGrid from "./SlotGrid";

const ProviderSlotGroup = ({
  firstName,
  lastName,
  credential,
  imageId,
  address,
  timezone,
  selectedSlotTime,
  slots,
  onSlotSelection,
  disabled,
}: {
  firstName: string;
  lastName: string;
  credential?: string;
  imageId: string;
  address: {street: string; city: string};
  timezone: string;
  selectedSlotTime?: number;
  slots: ApptSlot[];
  onSlotSelection: (slot: ApptSlot) => unknown;
  disabled?: boolean;
}) => {
  const {t} = useTranslation(I18nNamespace.WEB_SCHEDULING);
  const disclosure = useDisclosure();
  const cutoff = useTwMediaQuery(Breakpoint.SM) ? 8 : 6;

  const displayedSlots = useMemo(() => {
    return disclosure.isOpen ? slots : slots.slice(0, cutoff);
  }, [disclosure.isOpen, slots, cutoff]);

  const showMore = slots.length > cutoff && displayedSlots.length < slots.length;
  const showFewer = slots.length > cutoff && displayedSlots.length === slots.length;

  return (
    <div className="flex flex-col gap-4 w-full">
      <ProviderHeader
        firstName={firstName}
        lastName={lastName}
        credential={credential || ""}
        imageId={imageId || ""}
        address={address}
        alt=""
      />
      <SlotGrid
        onSlotSelection={onSlotSelection}
        selectedSlotTime={selectedSlotTime}
        slots={displayedSlots}
        timezone={timezone}
        disabled={disabled}
      />
      {showMore || showFewer ? (
        <div>
          <Button size="xs" variant="outline" onClick={disclosure.toggle}>
            {disclosure.isOpen
              ? t("Show fewer times", {ns: "web-scheduling"})
              : t("Show more times", {ns: "web-scheduling"})}
          </Button>
        </div>
      ) : null}
    </div>
  );
};

export const Loading = React.memo(() => {
  return (
    <div className="flex flex-col gap-4 w-full">
      <div style={{height: 76}}>
        <Glimmer offset={GlimmerOffset.XXS} height={76} width={60} />
      </div>
      <SlotGrid loading />
    </div>
  );
});

Loading.displayName = "ProviderSlotGroup.Loading";

export default React.memo(ProviderSlotGroup);
